import { useEffect, useState } from "react";
import {
  ViewModel,
  serverVMToClientVM,
  toServerVM,
  viewModelNewValues,
} from "./viewModel";
import { FormProvider, useForm } from "react-hook-form";
import getClassMetadata from "../../../Common/classMetadataService";
import LoadingContentWrapper from "../../../Common/Loading/LoadingContentWrapper";
import TextInputFormRow from "../../../Common/EditForm/TextInput/TextInputFormRow";
import getTextMetadata from "../../../Common/EditForm/ClassMetadata/getTextMetadata";
import SaveSuccess from "./SaveSuccess";
import FormButtons from "../../../Common/EditForm/formButtons";
import { useNavigate } from "react-router-dom";
import SaveError from "../../../Common/EditForm/SaveError";
import { httpPostNoReturn } from "../../../Common/Http/httpPostNoReturn";
import httpGet from "../../../Common/Http/httpGet";
import { EInvoiceConsentDataServerVM } from "./eInvoiceConsentDataServerVM";
import CheckBoxFormRow from "../../../Common/EditForm/CheckBox/CheckBoxFormRow";

export default function ChangeAccountDataForm(props: { backUrl?: string }) {
  let [isLoading, setIsLoading] = useState<boolean>(true);
  let [isSaveError, setIsSaveError] = useState<boolean>(false);
  let [isSaveSuccessful, setIsSaveSuccessful] = useState<boolean>(false);
  let [metadata, setMetadata] = useState<{ [key: string]: any }>({});

  const navigate = useNavigate();

  const methods = useForm<ViewModel>({
    mode: "onTouched",
    defaultValues: viewModelNewValues,
  });

  const { handleSubmit, formState, control, getValues, watch, trigger, reset } =
    methods;

  const { isSubmitting } = formState;

  const onSubmit = async (values: ViewModel) => {
    // let vmToSave = Object.assign({}, viewModel, values); // if values contain all the necessary data, this step will be unnecessary
    let data = toServerVM(values);

    try {
      await httpPostNoReturn<EInvoiceConsentDataServerVM>(
        "/api/Consent/EInvoiceConsent/SetState",
        data
      );

      setIsSaveSuccessful(true);
    } catch (e) {
      setIsSaveError(true);
    }

    return;
  };

  const onInitialize = async () => {
    // load form values
    // fetch existing data for the form
    // load data for selects, etc.
    // this method is async so we can use await here

    let metadata_response = await getClassMetadata("EInvoiceDataVM");
    setMetadata(metadata_response);

    let data: EInvoiceConsentDataServerVM =
      await httpGet<EInvoiceConsentDataServerVM>(
        "/api/Consent/EInvoiceConsent/GetState"
      );

    let initialValues = serverVMToClientVM(data);
    reset(initialValues);

    setIsLoading(false);
  };

  useEffect(() => {
    onInitialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // we want to trigger revalidation of the email2 field every time the email1 changes
  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (name === "eMail1") {
        // only if the field is touched: this makes sense only when it is a new form, otherwise we should display an error immediatelly
        // Note: isSubmitted becomes true after the user attempts to submit, even if the validation fails; after that the validation messages are displayed and the field shoud be revalidated
        // Here isSubmitted is in practice not critical, because when a email2 is entered, the field is already touched
        if (formState.touchedFields.eMail2 || formState.isSubmitted) {
          trigger("eMail2");
        }
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch, formState]);

  // we want to trigger revalidation of the email1 field every time the email2 changes
  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (name === "eMail2") {
        // only if the field is touched: this makes sense only when it is a new form, otherwise we should display an error immediatelly
        // Note: isSubmitted becomes true after the user attempts to submit, even if the validation fails; after that the validation messages are displayed and the field shoud be revalidated
        // Here isSubmitted is critical
        if (formState.touchedFields.eMail1 || formState.isSubmitted) {
          trigger("eMail1");
        }
      }
    });
    return () => subscription.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watch, formState]);

  return (
    <>
      <div className="content-wrapper">
        <h1 className="border-bottom pb-2 mt-4 mb-5">Zgoda na e-faktury</h1>
        <LoadingContentWrapper isLoading={isLoading} isFullWidth={true}>
          {!isLoading && !isSaveError && !isSaveSuccessful && (
            <FormProvider {...methods}>
              <form noValidate>
                <TextInputFormRow
                  {...getTextMetadata<ViewModel>("eMail1", metadata["eMail1"])}
                  className="-app-medium-control"
                  label="E-mail1"
                  control={control}
                  rules={{
                    validate: {
                      atLeastOne: (v: any): string | undefined => {
                        let isEmail1Empty = !v;
                        // typeof v === 'string' evaluates to false also for null and undefined
                        if (typeof v === "string") {
                          v = v.trim();
                          isEmail1Empty = v === "";
                        }

                        // we can stop validation when the email1 field isn't empty
                        if (!isEmail1Empty) return undefined;

                        // check the second email
                        let email2: string | null = getValues("eMail2");

                        let isEmail2Empty = !email2;
                        // typeof v === 'string' evaluates to false also for null and undefined
                        if (typeof email2 === "string") {
                          email2 = email2.trim();
                          isEmail2Empty = email2 === "";
                        }
                        // we can stop validation when the email1 field isn't empty
                        if (!isEmail2Empty) return undefined;

                        return "Musisz podać co najmniej jeden adres e-mail.";
                      },
                    },
                  }}
                />
                <TextInputFormRow
                  {...getTextMetadata<ViewModel>("eMail2", metadata["eMail2"])}
                  className="-app-medium-control"
                  label="E-mail2"
                  control={control}
                  inlineDescription="Możesz podać drugi adres e-mail"
                  rules={{
                    validate: {
                      notTheSame: (v: any): string | undefined => {
                        // shortcut for empty field
                        if (!v) return undefined;
                        // validaton between fields - with useForm/getValues()
                        let email1: string | null = getValues("eMail1");
                        if (!email1) return undefined;

                        // normalize both
                        email1 = email1.trim().toLowerCase();
                        let email2 = v.trim().toLowerCase();

                        if (email1 === email2)
                          return "Adresy e-mail nie mogą być takie same.";

                        return undefined;
                      },
                    },
                  }}
                />

                <CheckBoxFormRow
                  {...getTextMetadata<ViewModel>(
                    "consent",
                    metadata["consent"]
                  )}
                  label="Zgoda"
                  longDescription={undefined}
                  inlineDescription="Zaznaczając pole akceptujesz wystawianie i przesyłanie faktur w formie elektronicznej przez SPRZEDAWCĘ zgodnie z art.2 pkt 32 oraz zgodnie z art. 106n ust.1 ustawy o podatku od towarów i usług."
                  bodyText="Zaznacz, aby wyrazić zgodę."
                  control={control}
                  rules={{
                    validate: {
                      isChecked: (v: any): string | undefined => {
                        // shortcut for empty field
                        if (!v)
                          return "Musisz zaznaczyć to polę by wyrazić zgodę.";
                        return undefined;
                      },
                    },
                  }}
                ></CheckBoxFormRow>

                <div className="row">
                  <div className="offset-sm-4 col-sm-8">
                    <div
                      className="alert alert-primary d-flex align-items-center"
                      role="alert"
                    >
                      <div className="flex-shrink-0 ms-3 me-4">
                        <i className="bi bi-lightbulb"></i>
                      </div>
                      <div>
                        Musisz wypełnić przynajmniej jedno z pól: E-mail1 lub
                        E-mail2. <br />
                        Wprowadzone zmiany zostaną uwzględnione w
                        ciągu kilku godzin.
                      </div>
                    </div>
                  </div>
                </div>

                <div className="row pt-2 pb-5">
                  <div className="offset-sm-4 col-sm-8">
                    <FormButtons
                      onSave={(e) => handleSubmit(onSubmit)(e)}
                      onCancel={(e) => navigate(props.backUrl || "/")}
                      isSubmitting={isSubmitting}
                    ></FormButtons>
                  </div>
                </div>
              </form>
            </FormProvider>
          )}
          {isSaveError && (
            <SaveError
              navigate={(url: string) => navigate(url)}
              backUrl={props.backUrl || "/"}
            />
          )}
          {isSaveSuccessful && <SaveSuccess />}
        </LoadingContentWrapper>
      </div>
    </>
  );
}
